<template>
    <div class="border border-grey-100 shadow-sm rounded-md">

        <div v-if="!isPromotion">
            <div class="relative flex flex-col overflow-hidden">

                <div class="relative h-64 xs:h-40 md:h-32 lg:h-40 xl:h-48 flex items-center justify-center overflow-hidden rounded-t">
                    <div class="absolute top-0 left-0 w-full h-full flex items-center justify-center">
                        <div class="flex items-center justify-center py-24 text-gray-900 rounded w-1/2 opacity-25">
                            <div class="fal fa-circle-notch fa-2x fa-spin"></div>
                        </div>
                    </div>

                    <div class="absolute top-0 left-0 w-full h-full bg-center bg-cover" :style="{
                        'background-image': `url(${image})`,
                    }"></div>

                    <div class="w-full absolute flex h-full w-full flex-col gap-2 p-2 justify-end" :class="[hasSampleOrdering ? 'items-end xl:flex-row' : 'items-center']">
                        <div class="z-10 w-full" :class="{ 'xl:w-1/2': ! hasSampleOrdering }">
                            <a :href="quotationUrl" class="block rounded bg-orange-500 p-2 text-center text-xs text-white hover:bg-orange-600 shadow-lg h-full text-xs">
                                Quick quote
                            </a>
                        </div>
                        <div v-if="hasSampleOrdering" class="z-10 w-full">
                            <u-add-to-basket v-if="hasSampleOrdering" class="mx-auto mt-auto" name="Sample" type="sample" :parent="item.sku" :skuInBasket="item.skuInBasket" inline narrow small solid shadowed no-icon />
                        </div>
                    </div>

                    <a :href="url" class="absolute h-full w-full" :alt="alt"></a>
                </div>

                <div class="px-4 py-3">
                    <h1 class="text-sm text-gray-400 font-normal mb-2 uppercase tracking-wide leading-none" v-text="item.sku"></h1>
                    <h2 class="text-base text-gray-600 font-medium mb-2 leading-none" v-text="item.display_name"></h2>
                    <div v-if="! nonStandard" class="flex items-center" :class="{ 'mb-2': hasSampleOrdering }">
                        <div class="w-3 h-3 md:w-2 md:h-2 lg:w-3 lg:h-3 rounded-full" :class="{
                            'bg-green-500': inProduction,
                            'bg-orange-400': ! inProduction,
                        }"></div>
                        <p class="text-xs md:text-2xs lg:text-xs text-gray-900 opacity-75 font-medium uppercase tracking-wide ml-2 md:ml-1 lg:ml-2" v-text="production"></p>
                    </div>
                    <div v-if="nonStandard && item.pricing.per_brick" class="flex items-center" :class="{ 'mb-2': hasSampleOrdering }">
                        <p class="text-sm md:text-sm lg:text-sm text-gray-900 opacity-75 font-medium"><span v-text="price"></span> each</p>
                    </div>
                </div>

                <div class="w-full flex flex-col mt-auto px-4">
                    <div class="w-full flex items-center justify-start pb-3 text-left text-gray-400 text-sm font-normal">
                        <template v-if="hasVariations">
                            <div class="block md:hidden lg:block">Made in</div>
                            <div class="text-gray-500 font-bold mx-1 md:ml-0 lg:ml-1" v-text="item.variations.length"></div>
                            <div v-if="item.variations.length === 1">size</div>
                            <div v-else>sizes</div>
                        </template>
                        <template v-if="nonStandard">
                            <div class="text-gray-500 font-bold" v-text="quantity"></div>
                            <div class="ml-1">available</div>
                        </template>
                        <SampleBoard v-if="hasSampleOrdering" brick="#9babb4" class="ml-auto w-4"></SampleBoard>
                    </div>
                </div>
            </div>
        </div>

        <a v-else-if="promotionVariant === 'promo-tile-quotation'" href="/brick-quotation">
            <div class="flex flex-col justify-between rounded h-full min-h-40 promo-tile-quotation">
                <div class="text-promo text-lg sm:text-xl font-bold p-4">
                    On average, our customers save 20% by speaking to our experts.
                    Get in touch before buying bricks online
                </div>
                <div class="flex justify-end p-4 pt-0">
                    <img src="@/assets/images/logo-full-white.png" class="max-h-8">
                </div>
            </div>
        </a>

        <a v-else-if="promotionVariant === 'promo-tile-calculator'" href="/brick-estimation">
            <div class="flex flex-col justify-between rounded h-full min-h-40 promo-tile-calculator">
                <div class="text-promo text-lg sm:text-xl font-bold p-4">
                    Work out how many bricks you need before you place your order!
                    Check out our FREE brick calculator
                </div>
                <div class="flex justify-end p-4 pt-0">
                    <img src="@/assets/images/logo-full-white.png" class="max-h-8">
                </div>
            </div>
        </a>

        <a v-else-if="promotionVariant === 'promo-tile-matching'" href="/brick-matching">
            <div class="flex flex-col justify-between rounded h-full min-h-40 promo-tile-matching">
                <div class="text-promo text-lg sm:text-xl font-bold p-4">
                    Building an extension?
                    Find the best bricks for a seamless job with our expert Brick Matching service
                </div>
                <div class="flex justify-end p-4 pt-0">
                    <img src="@/assets/images/logo-full-colour.png" class="max-h-8">
                </div>
            </div>
        </a>
    </div>
</template>

<script>
    import SampleBoard from '../../Icons/SampleBoard.vue';

    export default {
        components: {
            SampleBoard,
        },

        props: {
            item: {
                type: Object,
                required: true,
            },
        },

        computed: {
            nonStandard() {
                return 'generic' !== this.item.saleTypeFlag;
            },

            inProduction() {
                return this.item.production.in_production;
            },

            production() {
                return this.inProduction ? 'In Production' : 'Not In Production';
            },

            url() {
                if ('clearance' === this.item.saleTypeFlag) {
                    return `/clearance-bricks/${this.item.slug}`;
                }

                if ('reclaimed' === this.item.saleTypeFlag) {
                    return `/reclaimed-bricks/${this.item.slug}`;
                }

                return `/bricks/${this.item.slug}`;
            },

            quotationUrl() {
                return `/brick-quotation?sku=${this.item.sku}`;
            },

            hasSampleOrdering() {
                return this.item.samples.available;
            },

            hasVariations() {
                return ! this.nonStandard;
            },

            image() {
                return this.item.primaryImage.url;
            },

            quantity() {
                return Number(this.item.meta.quantity).toLocaleString();
            },

            price() {
                return Number(this.item.pricing.per_brick / 100).toLocaleString('en-GB', {
                    style: 'currency',
                    currency: 'GBP'
                });
            },

            isPromotion() {
                return this.item.isPromotion;
            },

            promotionVariant() {
                return this.item.promotionVariant;
            },

            alt() {
                return `${this.item.name} product page`;
            },
        },
    };
</script>

<style scoped>
    a {
        transition: box-shadow .15s ease-in-out;
    }
    .text-promo {
        font-family: Rubik, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Oxygen, Ubuntu, Cantarell, Fira Sans, Droid Sans, Helvetica Neue;
    }
    .promo-tile-quotation {
        background-color: #3FB33C;
        color: white;
    }
    .promo-tile-calculator {
        background-color: #F57E1C;
        color: white;
    }
    .promo-tile-matching {
        background-color: #ffffff;
        color: black;
    }
</style>
